import type React from "react";
import { Input } from "@planta-v2/pds-ui/input";
import { Button } from "@planta-v2/pds-ui/button";
import { Label } from "@planta-v2/pds-ui/label";
import { PlantaMark } from "../components/PlantaMark";
import { GoogleSsoButton } from "../components/GoogleSsoButton";
import { i18n } from "@frontend/i18n";
import { useLoginController } from "../controllers/useLoginController";

export interface LoginPageProps {
  readonly enableSso?: boolean;
}

export function LoginPage({
  enableSso = false,
}: LoginPageProps = {}): React.JSX.Element {
  const { state, actions } = useLoginController();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-10 flex flex-col items-center text-center">
          <PlantaMark className="h-10 w-auto text-foreground" />
          <h1 className="mt-8 font-serif text-pds-heading-xl font-normal tracking-tight text-foreground">
            {i18n.t("login.title")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {i18n.t("login.subtitle")}
          </p>
        </div>
        <form onSubmit={actions.handleSubmit} className="space-y-4">
          {state.isError && (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {state.errorMessage}
            </div>
          )}
          <div className="space-y-1.5">
            <Label
              htmlFor="email"
              className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
            >
              {i18n.t("login.email.label")}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={i18n.t("login.email.placeholder")}
              value={state.email}
              onChange={(e): void => {
                actions.setEmail(e.target.value);
              }}
              disabled={state.isPending}
              required
              autoComplete="email"
              className="h-11 rounded-full bg-card px-4"
            />
          </div>
          <div className="space-y-1.5">
            <Label
              htmlFor="password"
              className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
            >
              {i18n.t("login.password.label")}
            </Label>
            <Input
              id="password"
              type="password"
              placeholder={i18n.t("login.password.placeholder")}
              value={state.password}
              onChange={(e): void => {
                actions.setPassword(e.target.value);
              }}
              disabled={state.isPending}
              required
              autoComplete="current-password"
              className="h-11 rounded-full bg-card px-4"
            />
          </div>
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={state.isPending}
          >
            {state.isPending
              ? i18n.t("login.submitting")
              : i18n.t("login.submit")}
          </Button>
          {enableSso && (
            <>
              <div className="flex items-center gap-3 py-2">
                <span className="h-px flex-1 bg-border" aria-hidden="true" />
                <span className="text-pds-micro font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {i18n.t("login.orSeparator")}
                </span>
                <span className="h-px flex-1 bg-border" aria-hidden="true" />
              </div>
              <GoogleSsoButton returnUrl={state.ssoReturnUrl} />
            </>
          )}
        </form>
      </div>
    </div>
  );
}
